export const blogPosts = [
  {
    slug: "unibrain-erp-build-journey",
    title: "Building UniBrain ERP: From Concept to SaaS",
    created_at: "2025-11-20",
    cover_image: "/images/erp.png",
    excerpt:
      "A deep dive into how I designed and built a multi-tenant ERP system from scratch.",
    tags: ["ERP", "Backend", "SaaS"],
    content: `
## 🚀 Project Kickoff

The UniBrain ERP started as a solution to fix fragmented business operations.

### 📅 Day 1 — Planning & Architecture
- Defined system requirements
- Designed multi-tenant architecture
- Chose Django + DRF for backend

---

## 👥 Team Collaboration

### 💻 Remote Meetings
- Daily sync with online dev team
- Weekly architecture reviews
- Agile sprint planning

---

## ⚙️ Development Phase

### 🔐 Authentication System
- Implemented JWT authentication
- Role-based access control (Admin, Employee, Executive)

### 🧾 HR Leave Module
- Leave request workflow
- Approval & rejection system
- Audit logging

---

## 📊 Dashboard Engineering

- Built executive KPI endpoints
- Real-time analytics
- Clean UI with React + Tailwind

---

## 🧠 Key Lessons

- Multi-tenant systems require strict data isolation
- UI must reflect backend permissions
- Clean architecture = scalability

---

## 🔗 Final Result

A scalable SaaS-ready ERP system with real business impact.
    `,
  },

  {
    slug: "portfolio-website-build",
    title: "Designing My Developer Portfolio",
    created_at: "2025-12-16",
    cover_image: "/images/portfolio.png",
    excerpt:
      "How I built a modern portfolio with animations, performance, and clean UI.",

    content: `
## 🎯 Goal

Create a modern, responsive developer portfolio.

---

## 🛠 Tech Stack

- React (Vite)
- Tailwind CSS
- Framer Motion

---

## 🎨 UI Design Process

- Glassmorphism inspiration
- Clean spacing & typography
- Smooth animations

---

## ⚡ Features Built

- Animated sections
- Project showcase
- Blog system
- Contact integration

---

## 📚 Lessons Learned

- Simplicity wins over complexity
- Performance matters
- First impressions are everything

---

## 🔗 Live Deployment

Deployed on Vercel with optimized performance.
    `,
  },

{
  slug: "task-manager-api-build",
  title: "Building a Task Manager API with Laravel",
  created_at: "2026-03-31",
  cover_image: "/images/task-api-blog.png",
  excerpt:
    "Designing a structured REST API with strict business rules, status transitions, and real-world constraints.",

  tags: ["Laravel", "Backend", "API", "System Design"],

  content: `
## 🚀 Project Kickoff

The goal was to design a clean and structured task management API enforcing real-world business rules.

---

## 📅 Day 1 — Planning & API Design

- Defined core entities (Tasks)
- Designed RESTful endpoints
- Structured request/response format

Key decision:
- Keep API stateless and REST-compliant

---

## ⚙️ Backend Development

### 🧾 Task Creation

- Enforced validation:
  - Unique title per due date
  - Future or current due date only
  - Priority levels (low, medium, high)

---

### 🔄 Status Workflow System

Implemented strict transitions:

- pending → in_progress → done

Why this matters:
- Prevents inconsistent state changes
- Mimics real-world task lifecycle

---

### 🗑️ Deletion Rules

- Tasks can ONLY be deleted when completed

This ensures:
- Data integrity
- Prevents accidental loss of active work

---

## 📊 Reporting System

Built a daily report endpoint:

GET /api/tasks/report?date=YYYY-MM-DD

Returns:
- Tasks created
- Status distribution
- Daily activity insights

---

## 🌐 Deployment (Railway)

- Deployed using Railway (free tier)
- PostgreSQL used in production
- Environment variables configured securely

Challenge:
- Cold start delays due to free tier

---

## 🧠 Key Lessons

- Business rules are as important as CRUD
- Validation must be enforced at API level
- Status workflows improve system reliability
- Deployment environments differ from local setups

---

## 🔗 Live API

https://taskmanagerassignment-production.up.railway.app

⚠️ Note: May take a few seconds to respond due to free-tier hosting.
  `,
},


{
  slug: "task-manager-frontend-build",
  title: "Building a Task Manager Dashboard with Next.js",
  created_at: "2026-04-02",
  cover_image: "/images/task-frontend-blog.jpg",
  excerpt:
    "Designing a real-time task management UI with filtering, analytics, and seamless API integration using Next.js.",

  tags: ["Next.js", "Frontend", "Dashboard", "React"],

  content: `
## 🚀 Project Overview

This project focused on building a modern frontend dashboard to interact with a Laravel-based Task Manager API.

The goal:
- Provide a clean UI
- Enable real-time updates
- Visualize task data effectively

---

## 📅 Day 1 — UI Planning

- Defined dashboard layout
- Designed task table structure
- Planned filtering & search UX

Key focus:
- Simplicity + clarity
- Fast interactions

---

## ⚙️ Frontend Architecture

### 🧠 State Management

- Used React state for:
  - Task list
  - Filters
  - Search queries

- Ensured UI updates instantly after API actions

---

### 🔗 API Integration

Connected to Laravel backend using Axios:

- Fetch tasks
- Create tasks
- Update status
- Delete completed tasks

Challenge:
- Keeping UI in sync after updates

Solution:
- Re-fetch + optimistic UI updates

---

## 📊 Dashboard & Analytics

Implemented:

- Task statistics:
  - Total
  - Pending
  - In Progress
  - Done

- Data visualization using Recharts

Why this matters:
- Gives users quick insights
- Improves decision-making

---

## 🎯 Filtering & Search

- Filter by priority (Low / Medium / High)
- Search by title

Result:
- Fast task discovery
- Better usability for large datasets

---

## 🔄 Task Workflow UI

- Status transitions:
  - pending → in_progress → done

UI reflects backend rules:
- Disabled invalid transitions
- Prevented invalid actions

---

## 🎨 UX Enhancements

- Clean Tailwind UI
- Responsive layout
- Date formatting for readability
- Smooth interactions

---

## 🌐 Deployment

- Deployed on Vercel
- Connected to Railway backend

Challenge:
- Backend cold starts (free tier)

---

## 🧠 Key Lessons

- Frontend must respect backend business logic
- UI feedback is critical for user experience
- Data visualization improves usability
- API design affects frontend complexity

---

## 🔗 Live Demo

Frontend:
https://taskassistance.vercel.app

Backend:
https://taskmanagerassignment-production.up.railway.app/api/tasks
  `,
},
{
  slug: "holiday-card-backend-build",
  title: "Building an AI-Style Holiday Card Generator with Flask",
  created_at: "2026-04-05",
  cover_image: "/images/holiday_card.png",
  excerpt:
    "Designing an image-processing backend that generates personalized holiday cards using dynamic templates and overlays.",

  tags: ["Flask", "Backend", "Image Processing", "Python"],

  content: `
## 🚀 Project Idea

The goal was to build a system that generates personalized holiday cards using user input and dynamic templates.

---

## 📅 Day 1 — Concept & Design

- Accept user name + image
- Apply themed templates
- Return a generated card

Key challenge:
- Image composition logic

---

## ⚙️ Backend Architecture

Built using Flask:

- Lightweight API
- Handles file uploads
- Processes images dynamically

---

## 🖼️ Image Processing Pipeline

Using Pillow:

1. Load template
2. Resize user image
3. Overlay onto template
4. Add text (user name + quote)
5. Export final image

---

## 🎄 Holiday Detection Logic

Implemented automatic theme selection:

- Christmas → Dec 24–26
- Mother’s Day → Second Sunday of May
- Easter → Approximate range
- Default fallback

This makes the system dynamic without user input.

---

## 🎲 Randomization Engine

- Random background selection
- Random quotes per generation

Result:
- Every card feels unique

---

## 🔗 API Design

POST /generate

Input:
- name
- image

Output:
- Generated JPEG image

---

## 🌐 Deployment

- Deployed on Render
- Handled file uploads in production

Challenge:
- File handling + temp storage

---

## 🧠 Key Lessons

- Image processing requires careful positioning & scaling
- Backend can be creative, not just CRUD
- Randomization improves user experience
- File handling in production needs planning

---

## 🔮 Future Improvements

- Cloud image storage
- Template selection UI
- Real holiday API integration
  `,
},
{
  slug: "holiday-card-frontend-build",
  title: "Building a Holiday Card Generator UI with React",
  created_at: "2026-04-06",
  cover_image: "/images/front_card.png",
  excerpt:
    "Creating an interactive UI for uploading images, generating cards, and sharing personalized holiday content.",

  tags: ["React", "Frontend", "UX", "File Upload"],

  content: `
## 🚀 Project Goal

Build a simple and interactive UI for generating personalized holiday cards.

---

## 🎯 UX Design

Flow:

1. Enter name
2. Upload image
3. Preview image
4. Generate card
5. Download or share

Focus:
- Minimal friction
- Clear actions

---

## ⚙️ Frontend Logic

- Managed form state (name + file)
- Used Axios for API calls
- Handled file uploads via FormData

---

## 🖼️ Image Preview System

- Display uploaded image before submission
- Improves user confidence

---

## 🔗 API Integration

Connected to Flask backend:

- Send image + name
- Receive generated image

Challenge:
- Handling binary image response

---

## 📥 Download Feature

- Convert response into downloadable file
- Enable quick save

---

## 📲 Sharing Feature

- WhatsApp share integration
- Makes app more social

---

## 🎨 UI Considerations

- Simple layout
- Focus on main action
- Fast feedback after click

---

## 🌐 Deployment

- Deployed on Vercel / Netlify
- Connected to backend API

---

## 🧠 Key Lessons

- File uploads need careful handling in frontend
- UX flow is critical for user engagement
- API response types affect UI logic
- Simplicity improves usability

---

## 🔮 Future Improvements

- Drag & reposition image
- Template selection UI
- Better styling (Tailwind upgrade)
  `,
}

];